import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Typography, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { fetchItems } from '../api/items';
import { addItem } from '../store/cartSlice';
import ItemCard from './ItemCard';

const PAGE_LENGTH = 40;

/**
 * Scrollable grid of item cards.
 * Fetches items via React Query with search/pagination.
 */
export default function ItemGrid({ searchTerm, posProfile, priceList, itemGroup }) {
  const dispatch = useDispatch();
  const [start, setStart] = useState(0);

  // Reset pagination when search changes
  useEffect(() => {
    setStart(0);
  }, [searchTerm]);

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey: ['posItems', posProfile, priceList, itemGroup, searchTerm, start],
    queryFn: () =>
      fetchItems({
        start,
        pageLength: PAGE_LENGTH,
        searchTerm,
        posProfile,
        priceList,
        itemGroup,
      }),
    enabled: !!posProfile,
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const handleItemClick = (item) => {
    dispatch(addItem(item));
  };

  if (isLoading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Failed to load items. Please try again.</Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">
          {searchTerm ? `No items found for "${searchTerm}"` : 'No items available'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5 }}>
      <Grid container spacing={1.5}>
        {items.map((item, idx) => (
          <Grid item xs={6} sm={4} md={3} key={`${item.item_code}-${idx}`}>
            <ItemCard item={item} onClick={handleItemClick} />
          </Grid>
        ))}
      </Grid>

      {/* Load more */}
      {items.length >= PAGE_LENGTH && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Button size="small" onClick={() => setStart((s) => s + PAGE_LENGTH)}>
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}
